import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Loader2, CalendarDays, Users, MapPin, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusStyles = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  confirmed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
  completed: 'bg-blue-100 text-blue-800 border-blue-200',
};

const MyBookingsPage = () => {
  const { token } = useAuth();
  const { formatPrice } = useCurrency();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/guest/bookings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBookings(res.data);
      } catch (err) {
        console.error('Failed to fetch bookings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [token]);

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EDE4]" data-testid="my-bookings-page">
      <Navbar />
      <div className="h-20" />

      <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-8">
        <h1 className="text-2xl font-semibold mb-6" data-testid="my-bookings-title">My Bookings</h1>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#D65A31]" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-20" data-testid="no-bookings">
            <CalendarDays className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h2 className="text-lg font-medium text-gray-600 mb-2">No bookings yet</h2>
            <p className="text-gray-400 mb-6">Discover amazing dining experiences and make your first reservation.</p>
            <Link to="/">
              <Button className="bg-[#D65A31] hover:bg-[#B84A28] text-white" data-testid="explore-btn">
                Explore Experiences
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {bookings.map((b) => (
              <Link
                key={b.id}
                to={`/experience/${b.experience_id}`}
                className="block"
                data-testid={`booking-card-${b.id}`}
              >
                <div className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow overflow-hidden flex">
                  {b.experience_image && (
                    <img
                      src={b.experience_image}
                      alt={b.experience_title}
                      className="w-32 sm:w-44 h-auto object-cover hidden sm:block"
                    />
                  )}
                  <div className="flex-1 p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="font-semibold text-base line-clamp-1">{b.experience_title}</h3>
                      <Badge className={`shrink-0 capitalize ${statusStyles[b.status] || ''}`} data-testid={`booking-status-${b.id}`}>
                        {b.status}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {b.date && format(parseISO(b.date), 'MMM d, yyyy')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {b.guests} guest{b.guests > 1 ? 's' : ''}
                      </span>
                      {b.experience_location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {b.experience_location}
                        </span>
                      )}
                      {b.host_name && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          Hosted by {b.host_name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-semibold mt-2">Total: {formatPrice(b.total_price)}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default MyBookingsPage;
